import { imageIsAr, imageName, openBitmap, canvasToBlob } from './image-batch-utils.js';
import { formatBytes, saveBlob, trackEvent, copyText } from './extra-common.js';

const isAr = imageIsAr;
const t = (ar, en) => (isAr ? ar : en);

const root = document.getElementById('image-color-picker-app');
root.classList.add('ic-studio');
root.replaceChildren();

// Global State
const state = {
  items: [],
  activeIndex: -1,
  activeColor: { r: 37, g: 99, b: 235, hex: '#2563eb', x: 0, y: 0 },
  zoom: 1,
  pointerPos: { x: 0, y: 0, inCanvas: false }
};

// Color naming dictionary
const COLOR_NAMES = [
  { hex: '#000000', en: 'Black', ar: 'أسود' },
  { hex: '#ffffff', en: 'White', ar: 'أبيض' },
  { hex: '#808080', en: 'Gray', ar: 'رمادي' },
  { hex: '#334155', en: 'Slate', ar: 'رمادي داكن' },
  { hex: '#ef4444', en: 'Red', ar: 'أحمر' },
  { hex: '#dc2626', en: 'Crimson', ar: 'قرمزي' },
  { hex: '#b91c1c', en: 'Ruby', ar: 'ياقوتي' },
  { hex: '#f97316', en: 'Orange', ar: 'برتقالي' },
  { hex: '#ea580c', en: 'Burnt Orange', ar: 'برتقالي داكن' },
  { hex: '#f59e0b', en: 'Amber', ar: 'كهرماني' },
  { hex: '#eab308', en: 'Gold', ar: 'ذهبي' },
  { hex: '#fef08a', en: 'Lemon', ar: 'ليموني' },
  { hex: '#84cc16', en: 'Lime', ar: 'ليموني أخضر' },
  { hex: '#22c55e', en: 'Green', ar: 'أخضر' },
  { hex: '#16a34a', en: 'Forest Green', ar: 'أخضر غابي' },
  { hex: '#10b981', en: 'Emerald', ar: 'زمردي' },
  { hex: '#14b8a6', en: 'Teal', ar: 'أخضر مزرق' },
  { hex: '#06b6d4', en: 'Cyan', ar: 'سماوي' },
  { hex: '#0ea5e9', en: 'Sky Blue', ar: 'أزرق سماوي' },
  { hex: '#3b82f6', en: 'Blue', ar: 'أزرق' },
  { hex: '#2563eb', en: 'Royal Blue', ar: 'أزرق ملكي' },
  { hex: '#1d4ed8', en: 'Cobalt', ar: 'كوبالت' },
  { hex: '#1e3a8a', en: 'Navy', ar: 'كحلي' },
  { hex: '#6366f1', en: 'Indigo', ar: 'نيلي' },
  { hex: '#8b5cf6', en: 'Violet', ar: 'بنفسجي فاتح' },
  { hex: '#a855f7', en: 'Purple', ar: 'أرجواني' },
  { hex: '#d946ef', en: 'Fuchsia', ar: 'فوشيا' },
  { hex: '#ec4899', en: 'Pink', ar: 'وردي' },
  { hex: '#f43f5e', en: 'Rose', ar: 'وردي نضر' },
  { hex: '#78350f', en: 'Brown', ar: 'بني' },
  { hex: '#92400e', en: 'Caramel', ar: 'كراميل' },
  { hex: '#fde68a', en: 'Cream', ar: 'كريمي' },
  { hex: '#cbd5e1', en: 'Silver', ar: 'فضي' }
];

function hexToRgb(hex) {
  const clean = hex.replace('#', '');
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16)
  };
}

function getColorName(r, g, b) {
  let closest = COLOR_NAMES[0];
  let minDiff = Infinity;
  for (const c of COLOR_NAMES) {
    const rgb = hexToRgb(c.hex);
    const diff = Math.hypot(r - rgb.r, g - rgb.g, b - rgb.b);
    if (diff < minDiff) {
      minDiff = diff;
      closest = c;
    }
  }
  return isAr ? closest.ar : closest.en;
}

function rgbToHsl(r, g, b) {
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const d = max - min;
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === R) h = (((G - B) / d) % 6) * 60;
    else if (max === G) h = (((B - R) / d) + 2) * 60;
    else h = (((R - G) / d) + 4) * 60;
    if (h < 0) h += 360;
  }
  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
    formatted: `hsl(${Math.round(h)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`
  };
}

function rgbToCmyk(r, g, b) {
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const k = 1 - Math.max(R, G, B);
  if (k >= 1) return { c: 0, m: 0, y: 0, k: 100, formatted: 'cmyk(0%, 0%, 0%, 100%)' };
  const c = Math.round(((1 - R - k) / (1 - k)) * 100);
  const m = Math.round(((1 - G - k) / (1 - k)) * 100);
  const y = Math.round(((1 - B - k) / (1 - k)) * 100);
  const kVal = Math.round(k * 100);
  return { c, m, y, k: kVal, formatted: `cmyk(${c}%, ${m}%, ${y}%, ${kVal}%)` };
}

function getRelativeLuminance(r, g, b) {
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

function getContrastRatio(r, g, b, [tr, tg, tb]) {
  const lum1 = getRelativeLuminance(r, g, b);
  const lum2 = getRelativeLuminance(tr, tg, tb);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
}

// Color Quantization for Dominant Colors
function extractDominantColors(canvas, maxColors = 8) {
  try {
    const sampleSize = 120;
    const sampleCanvas = document.createElement('canvas');
    sampleCanvas.width = sampleSize;
    sampleCanvas.height = sampleSize;
    const ctx = sampleCanvas.getContext('2d');
    ctx.drawImage(canvas, 0, 0, sampleSize, sampleSize);
    const data = ctx.getImageData(0, 0, sampleSize, sampleSize).data;

    const bins = new Map();
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 128) continue; // skip transparent
      const r = (data[i] >> 4) << 4;
      const g = (data[i + 1] >> 4) << 4;
      const b = (data[i + 2] >> 4) << 4;
      const key = (r << 16) | (g << 8) | b;
      bins.set(key, (bins.get(key) || 0) + 1);
    }

    const sorted = Array.from(bins.entries()).sort((a, b) => b[1] - a[1]);
    const chosen = [];
    const minDistance = 42;

    for (const [key] of sorted) {
      const r = (key >> 16) & 0xff;
      const g = (key >> 8) & 0xff;
      const b = key & 0xff;

      const isDistinct = chosen.every((item) => {
        const rgb = hexToRgb(item.hex);
        return Math.hypot(r - rgb.r, g - rgb.g, b - rgb.b) >= minDistance;
      });

      if (isDistinct || chosen.length === 0) {
        const hex = `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
        chosen.push({ r, g, b, hex, name: getColorName(r, g, b) });
      }

      if (chosen.length >= maxColors) break;
    }
    return chosen;
  } catch {
    return [];
  }
}

// Hidden file input
const fileInput = document.createElement('input');
fileInput.type = 'file';
fileInput.accept = 'image/*';
fileInput.multiple = true;
fileInput.className = 'batch-file-input';
fileInput.style.display = 'none';

// Dropzone (Empty State)
const dropzone = document.createElement('div');
dropzone.className = 'dropzone batch-dropzone';
dropzone.setAttribute('role', 'button');
dropzone.setAttribute('tabindex', '0');
dropzone.setAttribute('aria-label', t('اختر أو اسحب صورًا لاستخراج الألوان', 'Choose or drop images to extract colors'));
dropzone.innerHTML = `
  <div class="dropzone-content-default">
    <svg class="dropzone-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
      <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242M12 12v9m0-9l-3 3m3-3l3 3"/>
    </svg>
    <p class="dropzone-title">${t('اسحب الصور هنا أو انقر للاختيار من جهازك', 'Drop images here or click to choose from your device')}</p>
    <p class="dropzone-subtitle">${t('استخراج فوري للألوان · تكبير دقيق Pixels · معالجة محلية خاصة 100%', 'Instant color extraction · Precision pixel zoom · 100% private local processing')}</p>
  </div>
`;



// Gallery Strip Bar (when images are loaded)
const galleryBar = document.createElement('header');
galleryBar.className = 'ic-gallery-bar';
galleryBar.hidden = true;

const galleryStrip = document.createElement('div');
galleryStrip.className = 'ic-gallery-strip';

const galleryActions = document.createElement('div');
galleryActions.className = 'ic-gallery-actions';

const addMoreBtn = document.createElement('button');
addMoreBtn.type = 'button';
addMoreBtn.className = 'btn-secondary btn-sm';
addMoreBtn.innerHTML = `
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
  <span>${t('إضافة صور', 'Add more')}</span>
`;
addMoreBtn.addEventListener('click', () => fileInput.click());

const resetAllBtn = document.createElement('button');
resetAllBtn.type = 'button';
resetAllBtn.className = 'btn-tertiary btn-sm';
resetAllBtn.textContent = t('تفريغ الكل', 'Clear all');
resetAllBtn.addEventListener('click', () => {
  state.items = [];
  state.activeIndex = -1;
  renderApp();
});

galleryActions.append(addMoreBtn, resetAllBtn);
galleryBar.append(galleryStrip, galleryActions);

// Workspace 2-Column Grid
const workspace = document.createElement('div');
workspace.className = 'ic-workspace-grid';
workspace.hidden = true;

// --- Left Column: Stage & Eyedropper Viewport ---
const stageCard = document.createElement('section');
stageCard.className = 'ic-stage-card';
stageCard.setAttribute('aria-label', t('منطقة اختيار اللون من الصورة', 'Image eyedropper area'));

const stageToolbar = document.createElement('div');
stageToolbar.className = 'ic-stage-toolbar';

const stageMeta = document.createElement('div');
stageMeta.className = 'ic-stage-meta';
const fileNameEl = document.createElement('strong');
fileNameEl.className = 'ic-filename';
const fileDimEl = document.createElement('span');
fileDimEl.className = 'ic-stat-pill';
const fileSizeEl = document.createElement('span');
fileSizeEl.className = 'ic-stat-pill';
stageMeta.append(fileNameEl, fileDimEl, fileSizeEl);

const stageControls = document.createElement('div');
stageControls.className = 'ic-stage-controls';

// Native EyeDropper API Button
const nativePickerBtn = document.createElement('button');
nativePickerBtn.type = 'button';
nativePickerBtn.className = 'btn-secondary btn-sm';
nativePickerBtn.title = t('التقاط لون من أي مكان على شاشتك', 'Pick any color from your screen (EyeDropper)');
nativePickerBtn.innerHTML = `
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
    <path d="m19 11-8-8-8.6 8.6a2 2 0 0 0 0 2.8l5.2 5.2c.8.8 2 .8 2.8 0L19 11Z"/>
    <path d="m5 2 5 5"/>
    <path d="m2 5 5 5"/>
    <circle cx="2" cy="22" r="2"/>
  </svg>
  <span>${t('منتقي الشاشة', 'Screen Eyedropper')}</span>
`;
if (!('EyeDropper' in window)) {
  nativePickerBtn.style.display = 'none';
} else {
  nativePickerBtn.addEventListener('click', async () => {
    try {
      const eyeDropper = new window.EyeDropper();
      const res = await eyeDropper.open();
      if (res && res.sRGBHex) {
        const rgb = hexToRgb(res.sRGBHex);
        selectColor(rgb.r, rgb.g, rgb.b, 0, 0, true);
      }
    } catch {
      // User cancelled picker
    }
  });
}

// Zoom Controls
const zoomOutBtn = document.createElement('button');
zoomOutBtn.type = 'button';
zoomOutBtn.className = 'ic-btn-icon';
zoomOutBtn.title = t('تصغير (-)', 'Zoom Out');
zoomOutBtn.textContent = '−';
zoomOutBtn.addEventListener('click', () => adjustZoom(-0.25));

const zoomLabel = document.createElement('span');
zoomLabel.className = 'ic-zoom-label';
zoomLabel.textContent = '100%';

const zoomInBtn = document.createElement('button');
zoomInBtn.type = 'button';
zoomInBtn.className = 'ic-btn-icon';
zoomInBtn.title = t('تكبير (+)', 'Zoom In');
zoomInBtn.textContent = '+';
zoomInBtn.addEventListener('click', () => adjustZoom(0.25));

const zoomResetBtn = document.createElement('button');
zoomResetBtn.type = 'button';
zoomResetBtn.className = 'ic-btn-icon';
zoomResetBtn.title = t('ملاءمة للشاشة', 'Fit to screen');
zoomResetBtn.innerHTML = `
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
    <polyline points="15 3 21 3 21 9"></polyline>
    <polyline points="9 21 3 21 3 15"></polyline>
    <line x1="21" y1="3" x2="14" y2="10"></line>
    <line x1="3" y1="21" x2="10" y2="14"></line>
  </svg>
`;
zoomResetBtn.addEventListener('click', () => {
  state.zoom = 1;
  updateZoomUI();
});

stageControls.append(nativePickerBtn, zoomOutBtn, zoomLabel, zoomInBtn, zoomResetBtn);
stageToolbar.append(stageMeta, stageControls);

// Viewport container
const viewport = document.createElement('div');
viewport.className = 'ic-viewport';
viewport.setAttribute('tabindex', '0');
viewport.setAttribute('role', 'region');
viewport.setAttribute('aria-label', t('لوحة الصورة لاستخراج الألوان', 'Image canvas to pick color'));

const canvasWrap = document.createElement('div');
canvasWrap.className = 'ic-canvas-wrap';

const mainCanvas = document.createElement('canvas');
mainCanvas.className = 'ic-main-canvas';

// Precision Loupe Magnifier Element
const loupe = document.createElement('div');
loupe.className = 'ic-loupe';

const loupeCanvas = document.createElement('canvas');
loupeCanvas.className = 'ic-loupe-canvas';
loupeCanvas.width = 11;
loupeCanvas.height = 11;

const loupeReticle = document.createElement('div');
loupeReticle.className = 'ic-loupe-reticle';
const loupeCenterBox = document.createElement('div');
loupeCenterBox.className = 'ic-loupe-center-box';
loupeReticle.appendChild(loupeCenterBox);

const loupeBadge = document.createElement('div');
loupeBadge.className = 'ic-loupe-badge';
loupeBadge.textContent = '#2563eb';

loupe.append(loupeCanvas, loupeReticle, loupeBadge);
canvasWrap.append(mainCanvas, loupe);
viewport.appendChild(canvasWrap);

// Stage Bottom Hint
const stageHint = document.createElement('div');
stageHint.className = 'ic-stage-hint';
stageHint.innerHTML = `
  <span class="ic-hint-item">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 14 14"></polyline></svg>
    <span>${t('حرك المؤشر للمعاينة الدقيقة مع العدسة المكبرة (Loupe)', 'Hover to inspect with precision pixel loupe')}</span>
  </span>
  <span class="ic-hint-item">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>
    <span>${t('انقر في أي مكان لالتقاط وحفظ اللون في قائمتك', 'Click anywhere to pick and save color to palette')}</span>
  </span>
`;

stageCard.append(stageToolbar, viewport, stageHint);

// --- Right Column: Inspector & Palettes ---
const inspectorCol = document.createElement('aside');
inspectorCol.className = 'ic-inspector-col';

// 1. Active Swatch Card
const activeCard = document.createElement('div');
activeCard.className = 'ic-card';

const activeHeader = document.createElement('div');
activeHeader.className = 'ic-card-header';
const activeTitle = document.createElement('h3');
activeTitle.className = 'ic-card-title';
activeTitle.innerHTML = `
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><path d="m4.93 4.93 4.24 4.24"></path><path d="m14.83 9.17 4.24-4.24"></path><path d="m14.83 14.83 4.24 4.24"></path><path d="m9.17 14.83-4.24 4.24"></path></svg>
  <span>${t('اللون النشط المحدد', 'Active Sampled Color')}</span>
`;
activeHeader.appendChild(activeTitle);

const activePreview = document.createElement('div');
activePreview.className = 'ic-active-preview';
const activePreviewSolid = document.createElement('div');
activePreviewSolid.className = 'ic-active-preview-solid';
const activePreviewMeta = document.createElement('div');
activePreviewMeta.className = 'ic-active-preview-meta';
const colorNameEl = document.createElement('span');
colorNameEl.className = 'ic-color-name';
colorNameEl.textContent = 'Royal Blue';
const colorCoordsEl = document.createElement('span');
colorCoordsEl.className = 'ic-color-coords';
colorCoordsEl.textContent = 'X: 0, Y: 0';
activePreviewMeta.append(colorNameEl, colorCoordsEl);
activePreview.append(activePreviewSolid, activePreviewMeta);

// Values Grid
const valuesGrid = document.createElement('div');
valuesGrid.className = 'ic-values-grid';

function createValueRow(label, key) {
  const row = document.createElement('div');
  row.className = 'ic-val-row';
  const lbl = document.createElement('span');
  lbl.className = 'ic-val-label';
  lbl.textContent = label;
  const code = document.createElement('code');
  code.className = 'ic-val-code';
  code.dataset.key = key;

  const copyBtn = document.createElement('button');
  copyBtn.type = 'button';
  copyBtn.className = 'ic-val-copy-btn';
  copyBtn.innerHTML = `
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
    <span>${t('نسخ', 'Copy')}</span>
  `;
  copyBtn.addEventListener('click', async () => {
    try {
      await copyText(code.textContent);
      copyBtn.classList.add('copied');
      copyBtn.querySelector('span').textContent = t('تم!', 'Copied!');
      setTimeout(() => {
        copyBtn.classList.remove('copied');
        copyBtn.querySelector('span').textContent = t('نسخ', 'Copy');
      }, 1500);
    } catch {
      // fallback
    }
  });

  row.append(lbl, code, copyBtn);
  return row;
}

valuesGrid.append(
  createValueRow('HEX', 'hex'),
  createValueRow('RGB', 'rgb'),
  createValueRow('HSL', 'hsl'),
  createValueRow('CMYK', 'cmyk'),
  createValueRow('CSS', 'css')
);

// Contrast Check
const contrastRow = document.createElement('div');
contrastRow.className = 'ic-contrast-row';
contrastRow.innerHTML = `
  <div class="ic-contrast-pill white-text" id="ic-contrast-white">
    <span>${t('نص أبيض', 'White Text')}</span>
    <span class="ic-badge-score" id="ic-badge-white">--</span>
  </div>
  <div class="ic-contrast-pill dark-text" id="ic-contrast-dark">
    <span>${t('نص داكن', 'Dark Text')}</span>
    <span class="ic-badge-score" id="ic-badge-dark">--</span>
  </div>
`;

activeCard.append(activeHeader, activePreview, valuesGrid, contrastRow);

// 2. Dominant Palette Card
const dominantCard = document.createElement('div');
dominantCard.className = 'ic-card';

const dominantHeader = document.createElement('div');
dominantHeader.className = 'ic-card-header';
const dominantTitle = document.createElement('h3');
dominantTitle.className = 'ic-card-title';
dominantTitle.innerHTML = `
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
  <span>${t('ألوان الصورة البارزة (تلقائي)', 'Dominant Image Palette')}</span>
`;
const dominantCount = document.createElement('span');
dominantCount.className = 'ic-badge-count';
dominantCount.textContent = '0';
dominantHeader.append(dominantTitle, dominantCount);

const dominantGrid = document.createElement('div');
dominantGrid.className = 'ic-swatches-grid';
dominantCard.append(dominantHeader, dominantGrid);

// 3. User Sampled Colors (History)
const sampledCard = document.createElement('div');
sampledCard.className = 'ic-card';

const sampledHeader = document.createElement('div');
sampledHeader.className = 'ic-card-header';
const sampledTitle = document.createElement('h3');
sampledTitle.className = 'ic-card-title';
sampledTitle.innerHTML = `
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
  <span>${t('الألوان الملتقطة (محفوظة)', 'Sampled Colors')}</span>
`;
const sampledCount = document.createElement('span');
sampledCount.className = 'ic-badge-count';
sampledCount.textContent = '0';

const clearSampledBtn = document.createElement('button');
clearSampledBtn.type = 'button';
clearSampledBtn.className = 'btn-tertiary btn-xs';
clearSampledBtn.textContent = t('مسح', 'Clear');
clearSampledBtn.addEventListener('click', () => {
  const item = state.items[state.activeIndex];
  if (item) {
    item.sampledColors = [];
    renderSampledPalette();
  }
});

const sampledHeaderRight = document.createElement('div');
sampledHeaderRight.style.display = 'flex';
sampledHeaderRight.style.alignItems = 'center';
sampledHeaderRight.style.gap = '6px';
sampledHeaderRight.append(sampledCount, clearSampledBtn);

sampledHeader.append(sampledTitle, sampledHeaderRight);

const sampledGrid = document.createElement('div');
sampledGrid.className = 'ic-swatches-grid';
sampledCard.append(sampledHeader, sampledGrid);

// 4. Export Suite
const exportCard = document.createElement('div');
exportCard.className = 'ic-card';
const exportHeader = document.createElement('div');
exportHeader.className = 'ic-card-header';
const exportTitle = document.createElement('h3');
exportTitle.className = 'ic-card-title';
exportTitle.innerHTML = `
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
  <span>${t('تصدير لوحة الألوان', 'Export Palette')}</span>
`;
exportHeader.appendChild(exportTitle);

const exportActions = document.createElement('div');
exportActions.className = 'ic-export-actions';

const copyCssBtn = document.createElement('button');
copyCssBtn.type = 'button';
copyCssBtn.className = 'btn-secondary btn-sm ic-btn-export';
copyCssBtn.innerHTML = `
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>
  <span>${t('نسخ CSS Variables', 'Copy CSS Vars')}</span>
`;
copyCssBtn.addEventListener('click', () => exportAsCss(copyCssBtn));

const copyHexListBtn = document.createElement('button');
copyHexListBtn.type = 'button';
copyHexListBtn.className = 'btn-secondary btn-sm ic-btn-export';
copyHexListBtn.innerHTML = `
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
  <span>${t('نسخ قائمة HEX', 'Copy HEX List')}</span>
`;
copyHexListBtn.addEventListener('click', () => exportAsHexList(copyHexListBtn));

const exportJsonBtn = document.createElement('button');
exportJsonBtn.type = 'button';
exportJsonBtn.className = 'btn-secondary btn-sm ic-btn-export';
exportJsonBtn.innerHTML = `
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>
  <span>${t('تصدير JSON', 'Download JSON')}</span>
`;
exportJsonBtn.addEventListener('click', exportAsJson);

const exportImageBtn = document.createElement('button');
exportImageBtn.type = 'button';
exportImageBtn.className = 'btn-primary btn-sm ic-btn-export';
exportImageBtn.innerHTML = `
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
  <span>${t('بطاقة ألوان (PNG)', 'Palette Card (PNG)')}</span>
`;
exportImageBtn.addEventListener('click', exportPaletteCardPng);

exportActions.append(copyCssBtn, copyHexListBtn, exportJsonBtn, exportImageBtn);
exportCard.append(exportHeader, exportActions);

inspectorCol.append(activeCard, dominantCard, sampledCard, exportCard);
workspace.append(stageCard, inspectorCol);
root.append(fileInput, dropzone, galleryBar, workspace);

// --- Core Logic & Interactions ---

function adjustZoom(delta) {
  state.zoom = Math.max(0.5, Math.min(3, state.zoom + delta));
  updateZoomUI();
}

function updateZoomUI() {
  zoomLabel.textContent = `${Math.round(state.zoom * 100)}%`;
  canvasWrap.style.transform = `scale(${state.zoom})`;
}

function selectColor(r, g, b, x = 0, y = 0, saveToSampled = true) {
  const hex = `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
  state.activeColor = { r, g, b, hex, x, y };

  // Update Inspector UI
  activePreviewSolid.style.backgroundColor = hex;
  colorNameEl.textContent = getColorName(r, g, b);
  colorCoordsEl.textContent = `X: ${x}, Y: ${y} px`;

  const hsl = rgbToHsl(r, g, b);
  const cmyk = rgbToCmyk(r, g, b);

  const hexEl = valuesGrid.querySelector('[data-key="hex"]');
  const rgbEl = valuesGrid.querySelector('[data-key="rgb"]');
  const hslEl = valuesGrid.querySelector('[data-key="hsl"]');
  const cmykEl = valuesGrid.querySelector('[data-key="cmyk"]');
  const cssEl = valuesGrid.querySelector('[data-key="css"]');

  if (hexEl) hexEl.textContent = hex;
  if (rgbEl) rgbEl.textContent = `rgb(${r}, ${g}, ${b})`;
  if (hslEl) hslEl.textContent = hsl.formatted;
  if (cmykEl) cmykEl.textContent = cmyk.formatted;
  if (cssEl) cssEl.textContent = `color: ${hex};`;

  // Contrast check
  const contrastWhite = getContrastRatio(r, g, b, [255, 255, 255]);
  const contrastDark = getContrastRatio(r, g, b, [15, 23, 42]);

  const badgeWhite = document.getElementById('ic-badge-white');
  const badgeDark = document.getElementById('ic-badge-dark');

  if (badgeWhite) {
    const pass = contrastWhite >= 4.5;
    badgeWhite.className = `ic-badge-score ${pass ? 'pass' : 'fail'}`;
    badgeWhite.textContent = `${contrastWhite.toFixed(1)}:1 (${pass ? 'AA' : 'Fail'})`;
  }
  if (badgeDark) {
    const pass = contrastDark >= 4.5;
    badgeDark.className = `ic-badge-score ${pass ? 'pass' : 'fail'}`;
    badgeDark.textContent = `${contrastDark.toFixed(1)}:1 (${pass ? 'AA' : 'Fail'})`;
  }

  // Save to user sampled palette if requested
  const item = state.items[state.activeIndex];
  if (saveToSampled && item) {
    const alreadyExists = item.sampledColors.some((c) => c.hex.toLowerCase() === hex.toLowerCase());
    if (!alreadyExists) {
      item.sampledColors.unshift({ r, g, b, hex, x, y, name: getColorName(r, g, b) });
      renderSampledPalette();
    }
  }

  trackEvent('tool_completed', 'image-color-picker');
}

function updateLoupe(e) {
  const item = state.items[state.activeIndex];
  if (!item || !item.bitmap) return;

  const rect = mainCanvas.getBoundingClientRect();
  const clientX = e.touches ? e.touches[0].clientX : e.clientX;
  const clientY = e.touches ? e.touches[0].clientY : e.clientY;

  if (
    clientX < rect.left ||
    clientX > rect.right ||
    clientY < rect.top ||
    clientY > rect.bottom
  ) {
    loupe.style.display = 'none';
    return;
  }

  loupe.style.display = 'block';

  // Exact image coordinates
  const scaleX = item.bitmap.width / rect.width;
  const scaleY = item.bitmap.height / rect.height;
  const imgX = Math.max(0, Math.min(item.bitmap.width - 1, Math.floor((clientX - rect.left) * scaleX)));
  const imgY = Math.max(0, Math.min(item.bitmap.height - 1, Math.floor((clientY - rect.top) * scaleY)));

  // Position relative to canvas wrapper
  const wrapX = clientX - rect.left;
  const wrapY = clientY - rect.top;

  // Offset loupe so user's finger/mouse does not block the view
  const offset = 75;
  let loupeX = wrapX;
  let loupeY = wrapY - offset;

  if (loupeY < 65) loupeY = wrapY + offset; // flip below if near top
  if (loupeX < 65) loupeX = 65;
  if (loupeX > rect.width - 65) loupeX = rect.width - 65;

  loupe.style.left = `${loupeX}px`;
  loupe.style.top = `${loupeY}px`;

  // Draw 11x11 zoomed patch into loupe canvas
  const lctx = loupeCanvas.getContext('2d', { willReadFrequently: true });
  lctx.imageSmoothingEnabled = false;
  lctx.clearRect(0, 0, 11, 11);

  const startX = imgX - 5;
  const startY = imgY - 5;

  // Draw from main canvas onto loupe canvas
  lctx.drawImage(
    mainCanvas,
    startX,
    startY,
    11,
    11,
    0,
    0,
    11,
    11
  );

  // Center pixel data
  const centerData = lctx.getImageData(5, 5, 1, 1).data;
  const r = centerData[0];
  const g = centerData[1];
  const b = centerData[2];
  const hex = `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;

  loupeBadge.textContent = `${hex}`;
  loupe.style.boxShadow = `0 12px 32px rgba(0, 0, 0, 0.45), 0 0 0 3px #ffffff, 0 0 0 5px ${hex}`;

  // Live preview without adding to history yet
  selectColor(r, g, b, imgX, imgY, false);
}

// Canvas click / touch event handlers
mainCanvas.addEventListener('mousemove', updateLoupe);
mainCanvas.addEventListener('mouseleave', () => {
  loupe.style.display = 'none';
});

mainCanvas.addEventListener('click', (e) => {
  const item = state.items[state.activeIndex];
  if (!item || !item.bitmap) return;
  const rect = mainCanvas.getBoundingClientRect();
  const scaleX = item.bitmap.width / rect.width;
  const scaleY = item.bitmap.height / rect.height;
  const imgX = Math.max(0, Math.min(item.bitmap.width - 1, Math.floor((e.clientX - rect.left) * scaleX)));
  const imgY = Math.max(0, Math.min(item.bitmap.height - 1, Math.floor((e.clientY - rect.top) * scaleY)));

  const ctx = mainCanvas.getContext('2d', { willReadFrequently: true });
  const pixel = ctx.getImageData(imgX, imgY, 1, 1).data;
  selectColor(pixel[0], pixel[1], pixel[2], imgX, imgY, true);
});

// Touch support for mobile devices
mainCanvas.addEventListener('touchstart', (e) => {
  e.preventDefault();
  updateLoupe(e);
}, { passive: false });

mainCanvas.addEventListener('touchmove', (e) => {
  e.preventDefault();
  updateLoupe(e);
}, { passive: false });

mainCanvas.addEventListener('touchend', (e) => {
  loupe.style.display = 'none';
  if (state.activeColor) {
    const item = state.items[state.activeIndex];
    if (item) {
      const alreadyExists = item.sampledColors.some((c) => c.hex.toLowerCase() === state.activeColor.hex.toLowerCase());
      if (!alreadyExists) {
        item.sampledColors.unshift({ ...state.activeColor });
        renderSampledPalette();
      }
    }
  }
});

// Keyboard navigation (Arrow keys nudge by 1px)
viewport.addEventListener('keydown', (e) => {
  const item = state.items[state.activeIndex];
  if (!item || !item.bitmap) return;

  if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' '].includes(e.key)) {
    e.preventDefault();
    const step = e.shiftKey ? 10 : 1;
    let newX = state.activeColor.x;
    let newY = state.activeColor.y;

    if (e.key === 'ArrowLeft') newX = Math.max(0, newX - step);
    if (e.key === 'ArrowRight') newX = Math.min(item.bitmap.width - 1, newX + step);
    if (e.key === 'ArrowUp') newY = Math.max(0, newY - step);
    if (e.key === 'ArrowDown') newY = Math.min(item.bitmap.height - 1, newY + step);

    const ctx = mainCanvas.getContext('2d', { willReadFrequently: true });
    const pixel = ctx.getImageData(newX, newY, 1, 1).data;
    const save = e.key === 'Enter' || e.key === ' ';
    selectColor(pixel[0], pixel[1], pixel[2], newX, newY, save);
  }
});

// Rendering Functions

function renderDominantPalette() {
  dominantGrid.replaceChildren();
  const item = state.items[state.activeIndex];
  if (!item) return;

  dominantCount.textContent = String(item.dominantColors.length);

  for (const c of item.dominantColors) {
    const swatch = document.createElement('div');
    swatch.className = 'ic-swatch-item';
    swatch.title = `${c.name} (${c.hex}) - ${t('انقر للتحديد والنسخ', 'Click to inspect & copy')}`;

    const colorBar = document.createElement('div');
    colorBar.className = 'ic-swatch-color';
    colorBar.style.backgroundColor = c.hex;

    const hexLabel = document.createElement('span');
    hexLabel.className = 'ic-swatch-hex';
    hexLabel.textContent = c.hex;

    swatch.append(colorBar, hexLabel);
    swatch.addEventListener('click', async () => {
      selectColor(c.r, c.g, c.b, 0, 0, false);
      try {
        await copyText(c.hex);
        hexLabel.textContent = t('تم!', 'Copied!');
        setTimeout(() => { hexLabel.textContent = c.hex; }, 1200);
      } catch {}
    });

    dominantGrid.appendChild(swatch);
  }
}

function renderSampledPalette() {
  sampledGrid.replaceChildren();
  const item = state.items[state.activeIndex];
  if (!item) return;

  sampledCount.textContent = String(item.sampledColors.length);

  if (item.sampledColors.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'ic-empty-palette';
    empty.textContent = t('انقر على أي نقطة في الصورة لتكبيرها وحفظ لونها هنا.', 'Click on the image to inspect and save colors here.');
    sampledGrid.appendChild(empty);
    return;
  }

  for (let i = 0; i < item.sampledColors.length; i++) {
    const c = item.sampledColors[i];
    const swatch = document.createElement('div');
    swatch.className = 'ic-swatch-item';
    swatch.title = `${c.name || ''} (${c.hex}) - X: ${c.x}, Y: ${c.y}`;

    const colorBar = document.createElement('div');
    colorBar.className = 'ic-swatch-color';
    colorBar.style.backgroundColor = c.hex;

    const hexLabel = document.createElement('span');
    hexLabel.className = 'ic-swatch-hex';
    hexLabel.textContent = c.hex;

    const delBtn = document.createElement('button');
    delBtn.type = 'button';
    delBtn.className = 'ic-swatch-del';
    delBtn.innerHTML = '&times;';
    delBtn.title = t('حذف هذا اللون', 'Remove this color');
    delBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      item.sampledColors.splice(i, 1);
      renderSampledPalette();
    });

    swatch.append(colorBar, hexLabel, delBtn);
    swatch.addEventListener('click', async () => {
      selectColor(c.r, c.g, c.b, c.x, c.y, false);
      try {
        await copyText(c.hex);
        hexLabel.textContent = t('تم!', 'Copied!');
        setTimeout(() => { hexLabel.textContent = c.hex; }, 1200);
      } catch {}
    });

    sampledGrid.appendChild(swatch);
  }
}

function renderGalleryStrip() {
  galleryStrip.replaceChildren();
  if (state.items.length <= 1) {
    galleryBar.hidden = true;
    return;
  }

  galleryBar.hidden = false;

  state.items.forEach((item, idx) => {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = `ic-gallery-chip ${idx === state.activeIndex ? 'active' : ''}`;

    const thumb = document.createElement('canvas');
    thumb.className = 'ic-gallery-thumb';
    thumb.width = 32;
    thumb.height = 32;
    const tctx = thumb.getContext('2d');
    if (item.bitmap) {
      tctx.drawImage(item.bitmap, 0, 0, 32, 32);
    }

    const name = document.createElement('span');
    name.className = 'ic-gallery-name';
    name.textContent = item.file.name;

    const closeBtn = document.createElement('span');
    closeBtn.className = 'ic-gallery-close';
    closeBtn.innerHTML = '&times;';
    closeBtn.title = t('إغلاق هذه الصورة', 'Close this image');
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      state.items.splice(idx, 1);
      if (state.activeIndex >= state.items.length) {
        state.activeIndex = state.items.length - 1;
      }
      renderApp();
    });

    chip.append(thumb, name, closeBtn);
    chip.addEventListener('click', () => {
      state.activeIndex = idx;
      renderApp();
    });

    galleryStrip.appendChild(chip);
  });
}

function renderActiveItem() {
  const item = state.items[state.activeIndex];
  if (!item || !item.bitmap) return;

  fileNameEl.textContent = item.file.name;
  fileNameEl.title = item.file.name;
  fileDimEl.textContent = `${item.bitmap.width} × ${item.bitmap.height} px`;
  fileSizeEl.textContent = formatBytes(item.file.size);

  // Draw main image on canvas
  mainCanvas.width = item.bitmap.width;
  mainCanvas.height = item.bitmap.height;
  const ctx = mainCanvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(item.bitmap, 0, 0);

  // Extract dominant colors if not already done
  if (!item.dominantColors || !item.dominantColors.length) {
    item.dominantColors = extractDominantColors(mainCanvas, 8);
  }

  // Set initial active color from first dominant color or center pixel
  if (item.dominantColors.length && (!state.activeColor || state.activeColor.hex === '#2563eb')) {
    const c = item.dominantColors[0];
    selectColor(c.r, c.g, c.b, 0, 0, false);
  } else {
    const centerX = Math.floor(item.bitmap.width / 2);
    const centerY = Math.floor(item.bitmap.height / 2);
    const p = ctx.getImageData(centerX, centerY, 1, 1).data;
    selectColor(p[0], p[1], p[2], centerX, centerY, false);
  }

  renderDominantPalette();
  renderSampledPalette();
  state.zoom = 1;
  updateZoomUI();
}

function renderApp() {
  if (state.items.length === 0) {
    dropzone.hidden = false;
    galleryBar.hidden = true;
    workspace.hidden = true;
    return;
  }

  dropzone.hidden = true;
  workspace.hidden = false;
  renderGalleryStrip();
  renderActiveItem();
}

// Export Functions

async function exportAsCss(btn) {
  const item = state.items[state.activeIndex];
  if (!item) return;

  const allColors = [...item.dominantColors, ...item.sampledColors];
  if (!allColors.length) return;

  const lines = [':root {'];
  allColors.forEach((c, i) => {
    lines.push(`  --color-${i + 1}: ${c.hex}; /* ${c.name || 'Sample'} */`);
  });
  lines.push('}');

  try {
    await copyText(lines.join('\n'));
    btn.classList.add('copied');
    btn.querySelector('span').textContent = t('تم نسخ CSS!', 'CSS Copied!');
    setTimeout(() => {
      btn.classList.remove('copied');
      btn.querySelector('span').textContent = t('نسخ CSS Variables', 'Copy CSS Vars');
    }, 1500);
  } catch {}
}

async function exportAsHexList(btn) {
  const item = state.items[state.activeIndex];
  if (!item) return;

  const allColors = [...item.dominantColors, ...item.sampledColors];
  if (!allColors.length) return;

  const hexList = allColors.map((c) => c.hex).join(', ');
  try {
    await copyText(hexList);
    btn.classList.add('copied');
    btn.querySelector('span').textContent = t('تم نسخ القائمة!', 'List Copied!');
    setTimeout(() => {
      btn.classList.remove('copied');
      btn.querySelector('span').textContent = t('نسخ قائمة HEX', 'Copy HEX List');
    }, 1500);
  } catch {}
}

function exportAsJson() {
  const item = state.items[state.activeIndex];
  if (!item) return;

  const payload = {
    image: item.file.name,
    dimensions: { width: item.bitmap.width, height: item.bitmap.height },
    dominant_palette: item.dominantColors.map((c) => ({
      hex: c.hex,
      rgb: `rgb(${c.r}, ${c.g}, ${c.b})`,
      hsl: rgbToHsl(c.r, c.g, c.b).formatted,
      cmyk: rgbToCmyk(c.r, c.g, c.b).formatted,
      name: c.name
    })),
    sampled_colors: item.sampledColors.map((c) => ({
      hex: c.hex,
      rgb: `rgb(${c.r}, ${c.g}, ${c.b})`,
      hsl: rgbToHsl(c.r, c.g, c.b).formatted,
      cmyk: rgbToCmyk(c.r, c.g, c.b).formatted,
      x: c.x,
      y: c.y,
      name: c.name
    }))
  };

  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
  saveBlob(blob, `${imageName(item.file.name)}-palette.json`);
}

async function exportPaletteCardPng() {
  const item = state.items[state.activeIndex];
  if (!item || !item.bitmap) return;

  const colors = [...item.dominantColors, ...item.sampledColors].slice(0, 10);
  if (!colors.length) return;

  const card = document.createElement('canvas');
  card.width = 900;
  card.height = 500;
  const ctx = card.getContext('2d');

  // Background
  const grad = ctx.createLinearGradient(0, 0, 900, 500);
  grad.addColorStop(0, '#0f172a');
  grad.addColorStop(1, '#1e293b');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 900, 500);

  // Card border
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, 898, 498);

  // Brand Header
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 24px system-ui, sans-serif';
  ctx.fillText('Kazakan Tools — Color Palette', 40, 55);

  ctx.fillStyle = '#94a3b8';
  ctx.font = '14px system-ui, sans-serif';
  ctx.fillText(`${item.file.name} · ${item.bitmap.width}×${item.bitmap.height} px`, 40, 82);

  // Thumbnail Image on Left
  const thumbW = 280;
  const thumbH = 340;
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(40, 115, thumbW, thumbH, 12);
  ctx.clip();
  ctx.drawImage(item.bitmap, 40, 115, thumbW, thumbH);
  ctx.restore();

  // Draw border around thumbnail
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.roundRect(40, 115, thumbW, thumbH, 12);
  ctx.stroke();

  // Swatches on Right
  const startX = 360;
  const cols = 2;
  const rows = Math.ceil(colors.length / cols);
  const swatchW = 230;
  const swatchH = 58;
  const gapX = 16;
  const gapY = 12;

  colors.forEach((c, idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const x = startX + col * (swatchW + gapX);
    const y = 115 + row * (swatchH + gapY);

    // Swatch box
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(x, y, swatchW, swatchH, 10);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.06)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Color pill
    ctx.beginPath();
    ctx.roundRect(x + 10, y + 9, 40, 40, 8);
    ctx.fillStyle = c.hex;
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.stroke();

    // Hex and Name text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 15px monospace';
    ctx.fillText(c.hex.toUpperCase(), x + 62, y + 27);

    ctx.fillStyle = '#94a3b8';
    ctx.font = '12px system-ui, sans-serif';
    ctx.fillText(c.name || `RGB ${c.r}, ${c.g}, ${c.b}`, x + 62, y + 45);
    ctx.restore();
  });

  const blob = await canvasToBlob(card, 'image/png');
  saveBlob(blob, `${imageName(item.file.name)}-palette-card.png`);
}

// Image Loader
async function addImages(fileList) {
  const validFiles = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
  if (!validFiles.length) return;

  trackEvent('tool_started', 'image-color-picker');

  for (const file of validFiles) {
    try {
      const bitmap = await openBitmap(file);
      const item = {
        file,
        bitmap,
        dominantColors: [],
        sampledColors: []
      };
      state.items.push(item);
      if (state.activeIndex === -1) {
        state.activeIndex = state.items.length - 1;
      }
    } catch (err) {
      console.warn('Failed to load image:', err);
    }
  }

  renderApp();
}

// Dropzone Events
dropzone.addEventListener('click', () => {
  fileInput.click();
});

dropzone.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    fileInput.click();
  }
});

dropzone.addEventListener('dragover', (e) => {
  e.preventDefault();
  dropzone.classList.add('dragover', 'is-dragging');
});

dropzone.addEventListener('dragleave', () => {
  dropzone.classList.remove('dragover', 'is-dragging');
});

dropzone.addEventListener('drop', (e) => {
  e.preventDefault();
  dropzone.classList.remove('dragover', 'is-dragging');
  if (e.dataTransfer && e.dataTransfer.files) {
    addImages(e.dataTransfer.files);
  }
});

fileInput.addEventListener('change', () => {
  addImages(fileInput.files);
  fileInput.value = '';
});
